import { Component, input, OnInit, ViewChild, inject } from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { HttpErrorResponse } from '@angular/common/http';
import { AppModelModalComponent } from '@nimmsta/ng-commons-lib/forms';
import {
  AuthService,
  launchImpersonationInNewTab,
} from '@nimmsta/ng-commons-lib/auth';
import {
  Column,
  CustomAction,
  DataTableComponent,
  NimmstaDataSource,
  IconAction,
  EmptyTableComponent,
} from '@nimmsta/ng-commons-lib/data-table';
import {
  StatusBadgePipe,
  UserRolesPipe,
  DateTimePipe,
} from '@nimmsta/ng-commons-lib/pipes';
import { User, UserStatus } from '@nimmsta/ng-commons-lib/models';
import { UserService } from '@nimmsta/ng-commons-lib/services/user';
import { NimmstaCommonsModuleConfiguration } from '@nimmsta/ng-commons-lib/config';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import { UserFormComponent } from './user-form.component';
import { DeleteUserAction } from './deleteUserAction';
import { LockUnlockAction } from './lockUnlockAction';
import { ImpersonateAction } from './impersonateAction';
import { CheckSecondaryUserAction } from './checkSecondaryUserAction';

_('user.title');
@Component({
  selector: 'app-user',
  templateUrl: 'user.component.html',
  imports: [
    UserFormComponent,
    TranslatePipe,
    DataTableComponent,
    EmptyTableComponent,
    AppModelModalComponent,
    ErrorComponent,
  ],
  providers: [StatusBadgePipe, DateTimePipe, UserRolesPipe],
})
export class UserComponent implements OnInit {
  private userService = inject(UserService);
  private authService = inject(AuthService);
  private translate = inject(TranslateService);
  private statusBadge = inject(StatusBadgePipe);
  private configuration = inject(NimmstaCommonsModuleConfiguration);
  private dateTimePipe = inject(DateTimePipe);
  private userRolesPipe = inject(UserRolesPipe);

  userSource!: NimmstaDataSource<User>;

  @ViewChild(DataTableComponent, { static: true })
  private tableComponent!: DataTableComponent<User>;

  @ViewChild('EditModal', { static: false })
  private editModalComponent!: AppModelModalComponent<User>;

  @ViewChild(UserFormComponent, { static: true })
  private userFormComponent!: UserFormComponent;

  customActions: CustomAction<User>[] = [];

  iconActions: IconAction<User>[] = [];

  showAdminActions = input<boolean>(false);

  error?: HttpErrorResponse;

  get columns(): Column[] {
    return [
      new Column('family_name', 'user.family_name', { sortable: true }),
      new Column('given_name', 'user.given_name', { sortable: true }),
      new Column('email', 'user.email', { sortable: true }),
      new Column('roles', 'user.roles', { pipe: this.userRolesPipe }),
      new Column('status', 'user.status', {
        sortable: true,
        pipe: this.statusBadge,
      }),
      new Column('createdAt', 'user.createdAt', {
        pipe: this.dateTimePipe,
        sortable: true,
      }),
    ];
  }

  ngOnInit(): void {
    this.userSource = new NimmstaDataSource<User>(this.userService);
    if (
      this.showAdminActions() &&
      this.configuration.applicationUrls.workflow
    ) {
      const impersonateAction = new ImpersonateAction(
        'user.impersonate',
        this.authService,
        'cloud',
      );
      impersonateAction.subscribe((id) =>
        this.impersonate(
          id,
          new URL(this.configuration.applicationUrls.workflow!),
        ),
      );
      this.iconActions.push(impersonateAction);
    }
    const editAction = new CheckSecondaryUserAction('edit', false);
    editAction.subscribe((id) => this.edit(id));
    this.customActions.push(editAction);
    const deleteAction = new DeleteUserAction('delete', this.authService);
    deleteAction.subscribe((id) => this.delete(id));
    this.customActions.push(deleteAction);
    const removeAction = new CheckSecondaryUserAction(
      'user.secondary-user.remove',
      true,
    );
    removeAction.subscribe((id) => this.removeSecondaryOrg(id));
    this.customActions.push(removeAction);
    const lockUnlockAction = new LockUnlockAction('user.unlock');
    this.customActions.push(lockUnlockAction);
    lockUnlockAction.subscribe((id) => this.toggleLock(id));
  }

  new(): void {
    this.translate.get('user.new').subscribe((value) => {
      this.openModal(value, true, {} as User);
    });
  }

  edit(id: string): void {
    this.translate.get('user.edit').subscribe((value) => {
      this.openModal(value, true, this.getSelectedObject(id)!);
    });
  }

  openModal(title: string, editable: boolean, user: User): void {
    this.userFormComponent.editable = editable;
    this.userFormComponent.setValue(user);
    this.editModalComponent.title = title;
    this.editModalComponent.editable = editable;
    this.editModalComponent.show('sm');
  }

  delete(id: string): void {
    this.userService.delete(this.getSelectedObject(id)!).subscribe({
      next: () => {
        this.tableComponent.loadDataPage();
      },
      error: (error) => {
        this.error = error;
      },
    });
  }

  removeSecondaryOrg(id: string): void {
    this.userService.removeSecondaryOrg(id).subscribe({
      next: () => {
        this.tableComponent.loadDataPage();
      },
      error: (error) => {
        this.error = error;
      },
    });
  }

  toggleLock(id: string): void {
    const user = this.getSelectedObject(id)!;
    if (user.status === UserStatus.ACTIVE) {
      this.lock(id);
    } else {
      this.unlock(id);
    }
  }

  lock(id: string): void {
    this.userService.lock(this.getSelectedObject(id)!).subscribe({
      next: () => {
        this.tableComponent.loadDataPage();
      },
      error: (error) => {
        this.error = error;
      },
    });
  }

  unlock(id: string): void {
    this.userService.unlock(this.getSelectedObject(id)!).subscribe({
      next: () => {
        this.tableComponent.loadDataPage();
      },
      error: (error) => {
        this.error = error;
      },
    });
  }

  onSave(): void {
    this.userFormComponent.onSave();
  }

  onModalClose(): void {
    this.editModalComponent.hide();
    this.tableComponent.loadDataPage();
  }

  getSelectedObject(id: string): User | undefined {
    return this.userSource.data?.content.filter((d) => id === d.id).pop();
  }

  onLoading(loading: boolean): void {
    this.editModalComponent.loading = loading;
  }

  impersonate(id: string, target: URL): void {
    if (!this.showAdminActions()) {
      return;
    }
    this.userSource.data?.content
      .filter((d) => id === d.id)
      .forEach((user) => {
        this.authService.impersonate(user.email).subscribe({
          next: (data) => {
            const error = launchImpersonationInNewTab(
              this.authService,
              data,
              target,
            );
            if (error) {
              this.error = new HttpErrorResponse({
                error: { error: error.message },
              });
            }
          },
          error: (error) => {
            this.error = new HttpErrorResponse({
              ...error,
              error: { ...error.error, error: `httperror.${error.status}` },
            });
          },
        });
      });
  }
}
